import { Box } from '@mui/system';
import BackendService from 'BackendService';
import { CommonLoggedInDataContext, CommonLoggedInDataWithToken } from 'CommonLoggedInData';
import { GlobalSearchResult } from 'GlobalSearchResult';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import ContactDetails from '../../models/ContactDetails';
import { googleAnalyticsService } from '../../services/GoogleAnalyticsService';
import { withRouter } from '../helpers/withRouter';
import { StyledTextField } from '../styles/styles';
import HeaderGlobalSearchResults from './HeaderGlobalSearchResults';
import { resumeSupportLogging } from '../../services/admin/supportLoggingSession';
import HeaderImpersonationBanner from './HeaderImpersonationBanner';
import HeaderSearchTypeFilter from './HeaderSearchTypeFilter';
import SupportLoggingIndicator from './SupportLoggingIndicator';
import {
  DEFAULT_SELECTED_SEARCH_TYPES,
  getNavigationPathForSearchResult,
  getNextSearchResultIndex,
  getPreviousSearchResultIndex,
  getTypeFilterLabel,
  mapToSearchTypes,
  toggleSelectedSearchType,
} from './headerCenterContentUtils';
import {
  flushClientLogs,
  startClientLogCapture,
  stopClientLogCapture,
} from '../../services/clientLogCapture';

interface HeaderCenterContentProps {
  navigate: (path: string) => void;
}

interface HeaderCenterContentState {
  term: string;
  results: GlobalSearchResult[];
  selectedIndex: number;
  showResults: boolean;
  typeAnchorEl: HTMLElement | null;
  selectedTypes: string[];
  impersonatingName?: string;
  impersonationExpires?: number;
  impersonationCaptureLogs?: boolean;
}

class HeaderCenterContent extends React.Component<
  HeaderCenterContentProps,
  HeaderCenterContentState
> {
  static contextType = CommonLoggedInDataContext;
  context!: React.ContextType<typeof CommonLoggedInDataContext>;
  private searchRef = React.createRef<HTMLDivElement>();
  private inputRef = React.createRef<HTMLInputElement>();

  state: HeaderCenterContentState = {
    term: '',
    results: [],
    selectedIndex: -1,
    showResults: false,
    typeAnchorEl: null,
    selectedTypes: DEFAULT_SELECTED_SEARCH_TYPES,
    impersonatingName: undefined,
    impersonationExpires: undefined,
  };

  componentDidMount() {
    document.addEventListener('click', this.handleClickOutside);
    // The header is on every admin page, so a "Capture my logs" window carries on after a reload.
    resumeSupportLogging();
    new BackendService().getUserNameAndInitials().then(
      (
        data: ContactDetails & {
          impersonating: boolean;
          impersonationExpires: number;
          impersonationCaptureLogs: boolean;
          actorSub: string | null;
          targetSub: string | null;
        },
      ) => {
        const context = this.context as CommonLoggedInDataWithToken;
        if (context?.loggedInData?.email && googleAnalyticsService.getInitialized()) {
          googleAnalyticsService.setUserProperties({
            user_id: data.impersonating ? data.actorSub : context.loggedInData.email,
            impersonated_user_id: data.impersonating ? data.targetSub : null,
          });
        }

        if (data.impersonating) {
          this.setState({
            impersonatingName: data.name,
            impersonationExpires: data.impersonationExpires,
            impersonationCaptureLogs: data.impersonationCaptureLogs,
          });
          if (data.impersonationCaptureLogs) {
            startClientLogCapture((entries) =>
              new BackendService().sendImpersonationClientLogs(entries),
            );
          }
        }
      },
    );
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }

  handleClickOutside = (event: MouseEvent) => {
    if (this.searchRef.current && !this.searchRef.current.contains(event.target as Node)) {
      this.hideResults();
    }
  };

  hideResults = () => {
    this.setState({
      showResults: false,
      selectedIndex: -1,
    });
  };

  handleExit = () => {
    void flushClientLogs();
    stopClientLogCapture();
    new BackendService()
      .exitImpersonation()
      .then(() => window.close())
      .catch(() => window.close());
  };

  handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    this.setState({ term });

    if (term.trim()) {
      const types = mapToSearchTypes(this.state.selectedTypes);
      const results = await new BackendService().globalSearch(term, false, types, false);
      this.setState(
        {
          results,
          showResults: true,
          selectedIndex: -1,
        },
        () => this.inputRef.current?.focus(),
      );
    } else {
      this.setState(
        {
          results: [],
          showResults: false,
          selectedIndex: -1,
        },
        () => this.inputRef.current?.focus(),
      );
    }
  };

  openTypeMenu = (e: React.MouseEvent<HTMLElement>) => {
    this.setState({ typeAnchorEl: e.currentTarget });
  };

  closeTypeMenu = () => {
    this.setState({ typeAnchorEl: null });
  };

  toggleType = async (value: string) => {
    const next = toggleSelectedSearchType(this.state.selectedTypes, value);
    this.setState({ selectedTypes: next });

    if (this.state.term.trim()) {
      const results = await new BackendService().globalSearch(
        this.state.term,
        false,
        mapToSearchTypes(next),
        false,
      );
      this.setState({ results, showResults: true, selectedIndex: -1 }, () =>
        this.inputRef.current?.focus(),
      );
    }
  };

  handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { results, selectedIndex, showResults } = this.state;

    if (!showResults || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.setState({
          selectedIndex: getNextSearchResultIndex(selectedIndex, results.length),
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.setState({
          selectedIndex: getPreviousSearchResultIndex(selectedIndex, results.length),
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          this.handleResultClick(results[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        this.hideResults();
        if (this.inputRef.current) {
          this.inputRef.current.blur();
        }
        break;
    }
  };

  handleResultClick = (result: GlobalSearchResult) => {
    this.setState(
      {
        results: [],
        term: '',
        showResults: false,
        selectedIndex: -1,
      },
      () => {
        const path = getNavigationPathForSearchResult(result);
        if (path) {
          this.props.navigate(path);
        }
      },
    );
  };

  render() {
    const {
      term,
      results,
      selectedIndex,
      showResults,
      selectedTypes,
      typeAnchorEl,
      impersonatingName,
      impersonationExpires,
      impersonationCaptureLogs,
    } = this.state;

    const typeLabel = getTypeFilterLabel(selectedTypes);

    return (
      <>
        <Helmet>
          <style>
            {`
              header .MuiToolbar-root {
                flex-shrink: 0;
              }
              header .react-StyledToolbar {
                flex-shrink: 0;
              }
            `}
          </style>
        </Helmet>
        <Box
          ref={this.searchRef}
          sx={{
            flexGrow: 1,
            mx: { xs: 1, sm: 2, md: 0 },
            alignSelf: 'center',
            // Narrowed and truly centered within the header bar (not just
            // the leftover space between the logo and avatar, which aren't
            // equal widths) via absolute positioning against the AppBar -
            // the nearest positioned ancestor (it's `position: fixed`).
            // Left in normal flow on small screens, where there isn't room
            // to spare for centering.
            position: { xs: 'relative', md: 'absolute' },
            left: { md: '50%' },
            top: { md: '50%' },
            transform: { md: 'translate(-50%, -50%)' },
            width: { md: '100%' },
            maxWidth: { md: '600px' },
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {impersonatingName && (
            <HeaderImpersonationBanner
              impersonatingName={impersonatingName}
              impersonationExpires={impersonationExpires}
              captureLogs={impersonationCaptureLogs}
              onExit={this.handleExit}
            />
          )}
          <SupportLoggingIndicator />
          <StyledTextField
            ref={this.inputRef}
            size="small"
            variant="outlined"
            placeholder="Search..."
            value={term}
            onChange={this.handleChange}
            onKeyDown={this.handleKeyDown}
            fullWidth
            sx={{
              my: 0,
              backgroundColor: 'white',
              borderRadius: '999px',
              // Left/right insets matched at ~22px (pl here combines with
              // the adornment's own 4px margin and the type-filter button's
              // 16px margin to land the arrow ~22px from the pill edge).
              '& .MuiInputBase-root': { border: 'none', pl: '2px' },
              '& fieldset': { border: 'none' },
              '& .MuiInputBase-input': { pr: '22px' },
            }}
            inputRef={this.inputRef}
            InputProps={{
              startAdornment: (
                <HeaderSearchTypeFilter
                  typeLabel={typeLabel}
                  typeAnchorEl={typeAnchorEl}
                  selectedTypes={selectedTypes}
                  onOpenTypeMenu={this.openTypeMenu}
                  onCloseTypeMenu={this.closeTypeMenu}
                  onToggleType={this.toggleType}
                  onFocusSearch={() => this.searchRef.current?.querySelector('input')?.focus()}
                />
              ),
            }}
          />

          {showResults && results.length > 0 && (
            <HeaderGlobalSearchResults
              results={results}
              selectedIndex={selectedIndex}
              onResultClick={this.handleResultClick}
            />
          )}
        </Box>
      </>
    );
  }
}

export default withRouter(HeaderCenterContent);
